/*
 * <software-support-badge>
 * Server-side Liquid gates this element on the product's `ONLINESOFTWARE` tag and
 * ships it hidden. This component reveals it only for supported desktop/mobile OSes,
 * filling in the OS logo + copy client-side.
 *
 * Reveals for: Windows, macOS, iOS/iPadOS. Android / Linux / unknown stay hidden.
 *
 * Detection notes:
 *  - iPadOS 13+ reports as "macOS"; disambiguated via navigator.maxTouchPoints.
 *  - Android's userAgent also contains "Linux"; excluded explicitly.
 *  - Prefers navigator.userAgentData (Chromium) with a platform/UA fallback.
 */
(function () {
  if (customElements.get('software-support-badge')) return;

  // Static, self-authored SVG markup (never fetched / user-supplied) — safe to
  // assign via innerHTML. currentColor inherits the pill's text color.
  var LOGOS = {
    windows:
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M0 3.4 9.7 2.1v9.4H0V3.4Zm10.9-1.5L24 0v11.4H10.9V1.9ZM0 12.6h9.7V22L0 20.6v-8ZM10.9 12.6H24V24l-13.1-1.8v-9.6Z"/></svg>',
    apple:
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.365 1.43c0 1.14-.42 2.2-1.12 2.98-.75.83-1.98 1.47-3.02 1.39-.14-1.1.42-2.27 1.09-3 .75-.83 2.06-1.44 3.05-1.37zM20.9 17.5c-.55 1.27-.82 1.84-1.53 2.96-.99 1.57-2.39 3.53-4.12 3.54-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.01-3.05-1.78-4.04-3.35C-.02 17.1-.5 12.19 1.29 9.62c1.06-1.53 2.73-2.5 4.3-2.5 1.6 0 2.6 1.02 3.92 1.02 1.28 0 2.06-1.02 3.92-1.02 1.42 0 2.92.77 3.99 2.11-3.5 1.92-2.93 6.93.19 8.27z"/></svg>',
    // Stylized Tux — penguin body in currentColor, face marks in the theme
    // background color so it reads in both light and dark schemes.
    linux:
      '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2.2c-2.3 0-4 1.9-4 4.1 0 1.3-.2 2.1-.9 3-1.5 1.9-3 4-3 7.5 0 3.4 2.6 5.9 7.9 5.9s7.9-2.5 7.9-5.9c0-3.5-1.5-5.6-3-7.5-.7-.9-.9-1.7-.9-3 0-2.2-1.7-4.1-4-4.1z"/><path fill="currentColor" d="M8.7 21.3c-.9.7-2.1.6-2.5-.3l2.9-1zM15.3 21.3c.9.7 2.1.6 2.5-.3l-2.9-1z"/><circle cx="10.5" cy="6.4" r=".9" fill="rgb(var(--color-background))"/><circle cx="13.5" cy="6.4" r=".9" fill="rgb(var(--color-background))"/><path fill="rgb(var(--color-background))" d="M11 7.4h2l-1 1.3z"/></svg>'
  };

  // OS -> display. Only OSes present here ever reveal the badge; the `pos`
  // controls whether the logo sits before or after the copy.
  var OS_CONFIG = {
    windows: { text: 'Online software support for', logo: 'windows', pos: 'after' },
    linux:   { text: 'Online software support for', logo: 'linux',   pos: 'after' },
    mac:     { text: 'macOS compatible',            logo: 'apple',   pos: 'before' },
    ios:     { text: 'macOS compatible',            logo: 'apple',   pos: 'before' }
  };

  function detectPlatform() {
    var nav = window.navigator || {};
    var uaData = nav.userAgentData;
    var ua = nav.userAgent || '';
    var platform = (uaData && uaData.platform) || nav.platform || '';
    var touchPoints = nav.maxTouchPoints || 0;

    // iPadOS 13+ masquerades as desktop Safari on macOS.
    var isIpadOS = /Mac/i.test(platform) && touchPoints > 1;
    if (/iPhone|iPad|iPod/i.test(ua) || isIpadOS) return 'ios';

    if (/Mac/i.test(platform) || /Macintosh/i.test(ua)) return 'mac';

    // Android reports "Linux" in the UA string — must not count as Linux.
    if (/Android/i.test(ua)) return 'android';
    if (/Linux/i.test(platform) || /Linux/i.test(ua)) return 'linux';

    if (/Win/i.test(platform) || /Windows/i.test(ua)) return 'windows';
    return 'other';
  }

  class SoftwareSupportBadge extends HTMLElement {
    connectedCallback() {
      if (this.dataset.resolved) return;
      this.dataset.resolved = 'true';

      var os = detectPlatform();
      var config = OS_CONFIG[os];
      if (!config) return; // Android / Linux / unknown -> stays hidden

      // Optional per-placement allow-list (merchant control). Empty = all configured OSes.
      var raw = (this.getAttribute('data-platforms') || '').toLowerCase();
      if (raw) {
        var allowed = raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
        if (allowed.indexOf(os) === -1) return;
      }

      var logoEl = this.querySelector('[data-ss-logo]');
      if (logoEl) logoEl.innerHTML = LOGOS[config.logo] || '';

      var textEl = this.querySelector('[data-ss-text]');
      if (textEl) textEl.textContent = config.text;

      this.setAttribute('data-logo-pos', config.pos);
      this.hidden = false; // display:none -> inline-flex

      // A transition can't start from display:none, so let the browser commit the
      // layout for one frame before flipping data-os (which drives the fade-in).
      var el = this;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.setAttribute('data-os', os);
        });
      });
    }
  }

  customElements.define('software-support-badge', SoftwareSupportBadge);

  window.theme = window.theme || {};
  window.theme.detectPlatform = detectPlatform;
})();
